import { Container } from '../../../../../../widgets/layouts';
import { Dialog } from '../../../../../../widgets/dialog';
import React, { useMemo } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';
import { Title } from 'src/components/pages/login/layout';
import { merchantProduct_item$data } from 'src/components/pages/productConfiguration/state/queries/__generated__/merchantProduct_item.graphql';
import StoreCatalog from 'src/components/pages/productConfiguration/storeCatalog/storeCatalog';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import { RelayFragment } from 'src/shared/types';
import styled from 'styled-components';
import { ComposerAttributeNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerAttributeNodeAdapter } from '../helpers/composerNodesAdapter';
import { composerImportAttributeDialogAttributesQuery } from './__generated__/composerImportAttributeDialogAttributesQuery.graphql';
import { composerImportAttributeDialogModelIDQuery } from './__generated__/composerImportAttributeDialogModelIDQuery.graphql';

interface SceneObject {
    id: string;
    name: string;
}

interface SceneMaterial {
    id: string;
    name: string;
}

interface ObjectMatch {
    srcObject: SceneObject;
    dstObjects: SceneObject[];
}

interface MaterialMatch {
    srcMaterial: SceneMaterial;
    dstMaterial: SceneMaterial | null;
}

enum ComposerImportStep {
    SelectProduct = 0,
    SelectAttributes = 1,
    MatchObjectAndMaterials = 2
}

interface ComposerImportAttributesOutput {
    objectMatches: ObjectMatch[];
    materialMatches: MaterialMatch[];
}

interface ComposerImportAttributesDialogProps {
    salesChannelID: string;
    materialsList: SceneMaterial[];
    onConfirm: (attributes: ComposerImportAttributesOutput) => void;
}
const ComposerImportAttributeDialogWindow = styled(Dialog)`
    max-width: 600px;
`;
// New styled components for the step system
const StepContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 16px;
`;

const StepHeader = styled.div<{ $isActive: boolean; $isCompleted: boolean; $isDisabled: boolean }>`
    display: flex;
    padding: 12px 16px;
    background-color: ${({ $isActive, $isCompleted }) => ($isActive ? '#f0f0f0ff' : $isCompleted ? '#f0f0f0ff' : '#ffffff')};
    border: 1px solid ${theme.app.borderColor};
    border-radius: 4px;
    margin-bottom: 2px;
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $isDisabled }) => ($isDisabled ? theme.app.disabledOpacity : 1)};
`;

const StepNumber = styled.div<{ $isActive: boolean; $isCompleted: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${({ $isActive, $isCompleted }) => ($isCompleted ? theme.app.secondary : $isActive ? theme.app.primary : '#9e9e9e')};
    color: white;
    margin-right: 12px;
    font-size: 14px;
    font-weight: bold;
`;

const StepTitle = styled.div`
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 16px;
    flex-grow: 1;
`;

const StepContent = styled.div<{ isOpen: boolean }>`
    padding: ${({ isOpen }) => (isOpen ? '16px' : '0')};
    max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
    overflow: hidden;
    transition: all 0.3s ease;
    border: ${({ isOpen }) => (isOpen ? '1px solid #e0e0e0' : 'none')};
    border-top: none;
    border-radius: 0 0 4px 4px;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
`;

const StepContentInner = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ComposerImportAttributeList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
    margin: 8px 0;
`;

const ComposerImportAttributeItem = styled.div<{ $selected?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background-color: ${theme.app.backgroundColorBase};
    border: 1px solid ${theme.app.borderColor};
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: ${(props) => (props.$selected ? theme.app.secondary : theme.app.backgroundColor)};
    }

    ${(props) =>
        props.$selected &&
        `
		background-color: ${theme.app.secondary};
		color: ${theme.app.backgroundColor};
	`}
`;

const modelIDByCodeQuery = graphql`
    query composerImportAttributeDialogModelIDQuery($code: String!, $salesChannelId: String!) {
        modelIDByCode(code: $code, salesChannelId: $salesChannelId)
    }
`;

const attributesQuery = graphql`
    query composerImportAttributeDialogAttributesQuery($modelId: ID!) {
        model(id: $modelId) {
            attributes {
                id
                attributeID
                attributeGuid
                attributeCode
                attributeTypeID
                defaultValue
                displayOrder
                name
                description
                groupID
                groupGuid
                stepID
                stepGuid
                optionPreviewShapeID
                options {
                    optionID
                    optionGuid
                    optionCode
                    tempImageFileKey
                    tempImageUrl
                    imageUrl
                    tempImageFileObjectID
                    imageFileObjectID
                    name
                    description
                    displayOrder
                    isDefault
                    constraintExpression {
                        op
                        propositions {
                            attributeGuid
                            op
                            value
                        }
                    }
                    actions {
                        actionID
                        actionGuid
                        name
                        actionTypeID
                        sceneElementTypeID
                        selector
                        propertyName
                        value
                        execOrder
                        deviceTypes
                    }
                    extensionFields {
                        extensionFieldID
                        value
                        name
                        label
                        formatString
                        dataTypeID
                        operationTypeID
                    }
                }
                preActions {
                    actionID
                    actionGuid
                    name
                    actionTypeID
                    sceneElementTypeID
                    selector
                    propertyName
                    value
                    execOrder
                    deviceTypes
                }
                valueActions {
                    actionID
                    actionGuid
                    name
                    actionTypeID
                    sceneElementTypeID
                    selector
                    propertyName
                    value
                    execOrder
                    deviceTypes
                }
                postActions {
                    actionID
                    actionGuid
                    name
                    actionTypeID
                    sceneElementTypeID
                    selector
                    propertyName
                    value
                    execOrder
                    deviceTypes
                }
                meshSelector
                sceneCameraLocationID
                hideOptionsLabel
            }
        }
    }
`;

// Update your ComposerImportAttributesDialog component to use these components
const ComposerImportAttributesDialog: React.FC<ComposerImportAttributesDialogProps> = ({ salesChannelID, materialsList, onConfirm }) => {
    const environment = useRelayEnvironment();
    const [currentProduct, setCurrentProduct] = React.useState<Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'> | null>(null);
    const [objectMatches, setObjectMatches] = React.useState<ObjectMatch[]>([]);
    const [materialMatches, setMaterialMatches] = React.useState<MaterialMatch[]>([]);
    const [currentStep, setCurrentStep] = React.useState<ComposerImportStep>(ComposerImportStep.SelectProduct);

    const [productAttributes, setProductAttributes] = React.useState<ComposerAttributeNode[]>([]);
    const [selectedAttributes, setSelectedAttributes] = React.useState<ComposerAttributeNode[]>([]);

    const modelIdQuery = useMemo(async () => {
        if (!currentProduct || !currentProduct.sku) {
            return null;
        }
        return await fetchQuery<composerImportAttributeDialogModelIDQuery>(
            environment,
            modelIDByCodeQuery,
            {
                code: currentProduct.sku,
                salesChannelId: salesChannelID
            },
            {
                fetchPolicy: 'network-only'
                // skip: !modelId
            }
        ).toPromise();
    }, [currentProduct]);
    // Track completion of each step
    const [stepsCompleted, setStepsCompleted] = React.useState({
        [ComposerImportStep.SelectProduct]: false,
        [ComposerImportStep.SelectAttributes]: false,
        [ComposerImportStep.MatchObjectAndMaterials]: false
    });

    const handleStepClick = (step: ComposerImportStep) => {
        // Check if previous steps are completed or if this is the current step
        const canAccess =
            step === ComposerImportStep.SelectProduct ||
            (step === ComposerImportStep.SelectAttributes && stepsCompleted[ComposerImportStep.SelectProduct]) ||
            (step === ComposerImportStep.MatchObjectAndMaterials && stepsCompleted[ComposerImportStep.SelectAttributes]);

        if (canAccess) {
            setCurrentStep(step);
        }
    };

    const completeCurrentStep = () => {
        setStepsCompleted({
            ...stepsCompleted,
            [currentStep]: true
        });

        // Move to the next step if available
        if (currentStep < ComposerImportStep.MatchObjectAndMaterials) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleConfirm = () => {
        const importAttributes: ComposerImportAttributesOutput = {
            objectMatches,
            materialMatches
        };
        onConfirm(importAttributes);
    };

    const parseProductAttributes = (attributes: ComposerAttributeNode[]) => {};

    const handleProductClick = async (product: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => {
        setCurrentProduct(product);
        const query = await modelIdQuery;
        const attributesQueryData = await fetchQuery<composerImportAttributeDialogAttributesQuery>(
            environment,
            attributesQuery,
            { modelId: query?.modelIDByCode || '' },
            { fetchPolicy: 'network-only' }
        ).toPromise();
        setProductAttributes(
            ComposerAttributeNodeAdapter.adjustValues(
                (attributesQueryData?.model?.attributes || []).map((attr) => ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.fromJSON(attr)))
            )
        );
        parseProductAttributes(productAttributes);
        setStepsCompleted({
            [ComposerImportStep.SelectProduct]: true,
            [ComposerImportStep.SelectAttributes]: false,
            [ComposerImportStep.MatchObjectAndMaterials]: false
        });
        setObjectMatches([]); // Reset object matches when selecting a new product
        setMaterialMatches([]); // Reset material matches when selecting a new product
    };

    return (
        <Dialog windowDecorator={ComposerImportAttributeDialogWindow} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <Title>{T._('Import Attributes', 'Admin')}</Title>
            <Container>
                <StepContainer>
                    {/* Step 1 */}
                    <StepHeader
                        $isActive={currentStep === ComposerImportStep.SelectProduct}
                        $isCompleted={stepsCompleted[ComposerImportStep.SelectProduct]}
                        $isDisabled={false}
                        onClick={() => handleStepClick(ComposerImportStep.SelectProduct)}
                    >
                        <StepNumber
                            $isActive={currentStep === ComposerImportStep.SelectProduct}
                            $isCompleted={stepsCompleted[ComposerImportStep.SelectProduct]}
                        >
                            1
                        </StepNumber>
                        <StepTitle>
                            {T._('Select Product', 'Admin')}{' '}
                            {currentProduct && (
                                <>
                                    - {currentProduct?.name} ({currentProduct?.sku})
                                </>
                            )}
                        </StepTitle>
                    </StepHeader>
                    <StepContent isOpen={currentStep === ComposerImportStep.SelectProduct}>
                        <StepContentInner>
                            <StoreCatalog
                                cardTitle={T._('Select the products you want to import attributes from', 'Admin')}
                                salesChannelID={salesChannelID}
                                onProductClick={handleProductClick}
                                // selectedProductsSKUs={selectedProducts.map((x) => x.sku!)}
                                showHeader={false}
                                hideAlreadyConfiguredBadge={true}
                                showBackButton={false}
                                // disableProductCode={sourceProductCode}
                            />
                        </StepContentInner>
                    </StepContent>

                    {/* Step 2 */}
                    <StepHeader
                        $isActive={currentStep === ComposerImportStep.SelectAttributes}
                        $isCompleted={stepsCompleted[ComposerImportStep.SelectAttributes]}
                        $isDisabled={!stepsCompleted[ComposerImportStep.SelectProduct]}
                        onClick={() => handleStepClick(ComposerImportStep.SelectAttributes)}
                    >
                        <StepNumber
                            $isActive={currentStep === ComposerImportStep.SelectAttributes}
                            $isCompleted={stepsCompleted[ComposerImportStep.SelectAttributes]}
                        >
                            2
                        </StepNumber>
                        <StepTitle>{T._('Select Attributes', 'Admin')}</StepTitle>
                    </StepHeader>
                    <StepContent isOpen={currentStep === ComposerImportStep.SelectAttributes}>
                        <StepContentInner>
                            <ComposerImportAttributeList>
                                {productAttributes.map((attribute, _) => (
                                    <ComposerImportAttributeItem
                                        key={attribute.id}
                                        $selected={selectedAttributes.some((a) => a.id === attribute.id)}
                                        onClick={() => {
                                            setSelectedAttributes([attribute]);
                                            completeCurrentStep();
                                        }}
                                    >
                                        <span>{attribute.name}</span>
                                    </ComposerImportAttributeItem>
                                ))}
                            </ComposerImportAttributeList>
                        </StepContentInner>
                    </StepContent>

                    {/* Step 3 */}
                    <StepHeader
                        $isActive={currentStep === ComposerImportStep.MatchObjectAndMaterials}
                        $isCompleted={stepsCompleted[ComposerImportStep.MatchObjectAndMaterials]}
                        $isDisabled={!stepsCompleted[ComposerImportStep.SelectAttributes]}
                        onClick={() => handleStepClick(ComposerImportStep.MatchObjectAndMaterials)}
                    >
                        <StepNumber
                            $isActive={currentStep === ComposerImportStep.MatchObjectAndMaterials}
                            $isCompleted={stepsCompleted[ComposerImportStep.MatchObjectAndMaterials]}
                        >
                            3
                        </StepNumber>
                        <StepTitle>{T._('Match Objects & Materials', 'Admin')}</StepTitle>
                    </StepHeader>
                    <StepContent isOpen={currentStep === ComposerImportStep.MatchObjectAndMaterials}>
                        <StepContentInner></StepContentInner>
                    </StepContent>
                </StepContainer>
            </Container>
        </Dialog>
    );
};

export default ComposerImportAttributesDialog;
